'use client'
import { useCreditFormatters } from "@/hooks/use-credit-formatters"


import { useState, type ReactNode } from 'react'
import { CreditCard } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card'
import type { BillingOverviewResponse } from '@/lib/billing/types'
import { BalanceDetailsDialog, formatExpiryDate } from './balance-details-dialog'
import { useLocale, useTranslations } from "next-intl"


type Balance = BillingOverviewResponse['balance']

/** `children` renders under the amount (the Shopify plan-credits progress). */
export function BalanceCard({
  balance,
  timezone,
  children,
}: {
  balance: Balance
  timezone: string
  children?: ReactNode
}) {
  const locale = useLocale()
  const { formatCredits, formatCreditsExpiring } = useCreditFormatters()

  const tUi = useTranslations("Billing.BillingBalanceCard")

  const [detailsOpen, setDetailsOpen] = useState(false)

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardDescription className="flex items-center gap-1">
            <CreditCard className="h-4 w-4" /> <span><span key={tUi("balance")}>{tUi("balance")}</span></span>
          </CardDescription>
          {balance.grants.length > 0 && (
            <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={() => setDetailsOpen(true)}>
              <span key={tUi("details")}>{tUi("details")}</span></Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className={`break-words font-mono text-2xl font-bold ${balanceTone(balance)}`}>
          {formatCredits(balance.balanceMicrousd)}
        </div>
        {balance.nextExpiry && (
          <p className="mt-1 text-xs text-muted-foreground">{formatCreditsExpiring(balance.nextExpiry.amountMicrousd, formatExpiryDate(balance.nextExpiry.expiresAt, timezone, locale))}</p>
        )}
        {children}
        {balance.isDepleted && <p className="mt-1 text-xs text-destructive"><span key={tUi("balanceDepleted")}>{tUi("balanceDepleted")}</span></p>}
        {balance.isLow && !balance.isDepleted && <p className="mt-1 text-xs text-yellow-600"><span key={tUi("lowBalance")}>{tUi("lowBalance")}</span></p>}
      </CardContent>
      <BalanceDetailsDialog
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
        grants={balance.grants}
        timezone={timezone}
      />
    </Card>
  )
}

function balanceTone({ isDepleted, isLow }: Balance): string {
  if (isDepleted) return 'text-destructive'
  if (isLow) return 'text-yellow-600'
  return ''
}
